(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!('IntersectionObserver' in window)) {
    root.classList.add('za-scroll-reveal-disabled');
    const loadDeferredImages = () => {
      document.querySelectorAll('[data-za-deferred-image]').forEach((image) => {
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        if (image.dataset.src) image.src = image.dataset.src;
      });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', loadDeferredImages, { once: true });
    else loadDeferredImages();
    return;
  }

  root.classList.add('za-scroll-reveal-ready');

  const syncMotionPreference = () => {
    root.classList.toggle('za-scroll-reveal-reduced', reduceMotion.matches);
  };

  syncMotionPreference();

  const revealSelectors = [
    '.za-heading',
    '.za-collection-tile-v4',
    '.za-product-card',
    '.za-editorial-v4__image',
    '.za-editorial-v4__copy',
    '.za-world-feed__head > *',
    '.za-world-feed__item',
    '.za-world-feed__statement',
    '.za-support__item',
    '.za-collection-hero-v4__media',
    '.za-collection-hero-v4__copy',
    '.za-product__frame',
    '.za-product__info',
    '.za-pdp-style__media',
    '.za-pdp-style__copy',
    '.za-pdp-editorial > *',
    '.za-pdp-related .za-products',
    '.za-pdp-reviews',
    '.za-info-hero__inner',
    '.za-info-content > *',
    '.zoalin-info__card',
    '.zoalin-info__support',
    '.za-about-v2__hero',
    '.za-about-v2__image',
    '.za-about-v2__value',
    '.za-about-v2__story',
    '.za-about-v2__closing',
    '.za-cart-empty',
    '.zoalin-wishlist-page__empty',
    '.zoalin-wishlist-item',
    '.za-cart-item',
    '.za-cart-summary',
    '.za-account__intro',
    '.za-account__panel',
    '.za-contact-panel',
    '.za-contact-help',
    '.za-system-card',
    '.za-footer__brand',
    '.za-footer__columns > div',
    '.za-footer__localization',
    '.za-footer__feedback'
  ];

  const staggerGroups = [
    ['.za-collections-v4', '.za-collection-tile-v4', 80, 280],
    ['.za-products', '.za-product-card', 55, 330],
    ['.za-world-feed__rail', '.za-world-feed__item', 45, 270],
    ['.za-support', '.za-support__item', 70, 280],
    ['.za-product__media', '.za-product__frame', 65, 260],
    ['.za-footer__columns', ':scope > div', 75, 225],
    ['.za-about-v2__values', '.za-about-v2__value', 80, 240],
    ['.zoalin-wishlist-grid', '.zoalin-wishlist-item', 55, 330]
  ];

  const observed = new WeakSet();

  const isSkippable = (el) => {
    if (!(el instanceof HTMLElement)) return true;
    if (el.hidden || el.closest('[hidden], dialog:not([open]), .za-cart-drawer:not(.is-open)')) return true;
    if (el.matches('.za-header, .za-announcement, .za-actions, .za-nav, script, style')) return true;
    if (el.matches('.za-world-feed__item--clone')) return true;
    return false;
  };

  const setDelay = (el, delay) => {
    if (!el.style.getPropertyValue('--za-reveal-delay')) {
      el.style.setProperty('--za-reveal-delay', `${delay}ms`);
    }
  };

  const prepareStagger = (scope) => {
    staggerGroups.forEach(([parentSelector, childSelector, step, max]) => {
      scope.querySelectorAll(parentSelector).forEach((parent) => {
        parent.querySelectorAll(childSelector).forEach((item, index) => {
          setDelay(item, Math.min(index * step, max));
        });
      });
    });
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, {
    threshold: 0.14,
    rootMargin: '0px 0px -8% 0px'
  });

  const marqueeObserved = new WeakSet();
  const marqueeObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      entry.target.classList.toggle('is-marquee-active', entry.isIntersecting);
    });
  }, {
    threshold: 0.2,
    rootMargin: '160px 0px'
  });

  const hydrateImage = (image) => {
    if (!(image instanceof HTMLImageElement) || image.dataset.zaDeferredLoaded === 'true') return;
    const src = image.dataset.src;
    const srcset = image.dataset.srcset;
    if (srcset) image.srcset = srcset;
    if (src) image.src = src;
    image.dataset.zaDeferredLoaded = 'true';
    image.removeAttribute('data-src');
    image.removeAttribute('data-srcset');
  };

  const deferredImageObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      hydrateImage(entry.target);
      deferredImageObserver.unobserve(entry.target);
    });
  }, {
    threshold: 0.01,
    rootMargin: '700px 220px'
  });

  const fallbackPrepared = new WeakSet();
  const prepareImageFallback = (image) => {
    if (!(image instanceof HTMLImageElement) || fallbackPrepared.has(image)) return;
    fallbackPrepared.add(image);
    image.addEventListener('error', () => {
      const fallback = image.dataset.zaImageFallback;
      if (!fallback || image.dataset.zaImageFallbackApplied === 'true') return;
      image.dataset.zaImageFallbackApplied = 'true';
      image.removeAttribute('srcset');
      image.src = fallback;
    });
  };

  const observe = (scope = document) => {
    prepareStagger(scope);
    scope.querySelectorAll(revealSelectors.join(',')).forEach((el) => {
      if (isSkippable(el) || observed.has(el)) return;
      el.classList.add('za-reveal');
      if (
        el.matches('.za-collection-hero-v4__copy, .za-info-hero__inner, .za-account__panel')
      ) {
        el.classList.add('za-reveal--slow');
      }
      if (
        el.matches('.za-world-feed__item, .za-support__item, .za-footer__columns > div, .za-footer__localization')
      ) {
        el.classList.add('za-reveal--soft');
      }
      observed.add(el);
      observer.observe(el);
    });

    scope.querySelectorAll('.za-world-feed__marquee').forEach((marquee) => {
      if (marqueeObserved.has(marquee)) return;
      marqueeObserved.add(marquee);
      marqueeObserver.observe(marquee);
    });

    scope.querySelectorAll('[data-za-deferred-image]').forEach((image) => {
      if (image.dataset.zaDeferredObserved === 'true') return;
      image.dataset.zaDeferredObserved = 'true';
      deferredImageObserver.observe(image);
    });

    scope.querySelectorAll('[data-za-image-fallback]').forEach(prepareImageFallback);
  };

  const scheduleObserve = (() => {
    let frame = 0;
    return (scope = document) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => observe(scope));
    };
  })();

  const handleMotionPreferenceChange = () => {
    syncMotionPreference();
    scheduleObserve();
  };

  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', handleMotionPreferenceChange);
  } else if (typeof reduceMotion.addListener === 'function') {
    reduceMotion.addListener(handleMotionPreferenceChange);
  }

  document.addEventListener('DOMContentLoaded', () => scheduleObserve());
  window.addEventListener('pageshow', () => scheduleObserve());
  document.addEventListener('shopify:section:load', (event) => scheduleObserve(event.target));

  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.addedNodes.length)) {
        scheduleObserve();
      }
    }).observe(document.body || document.documentElement, { childList: true, subtree: true });
  }
})();
